import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Map from '../../components/Map';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import config from '../../config';
import { FaBuilding, FaCommentDots, FaMapMarkerAlt, FaRulerCombined, FaUsers } from 'react-icons/fa';

function TabActivityInfo({ onDataUpdated }) {
  const { id, years, fac_id, campus_id } = useParams();
  const [infos, setInfos] = useState([]);
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [employee, setEmployee] = useState('');
  const [area, setArea] = useState('');
  const [comment, setComment] = useState('');
  const [isDisabled, setIsDisabled] = useState(true);

  const fetchSystemOpen = useCallback(async () => {
    try {
      const yearAD = Number(years) - 543;
      const res = await axios.get(config.urlApi + `/activity/showPeriod/${fac_id}`);
      if (res.status === 200 && res.data.length > 0) {
        const currentPeriod = res.data.find((item) => Number(item.years) === yearAD);
        const isOpenRaw = currentPeriod?.is_open;
        const isOpen = isOpenRaw === true || isOpenRaw === 1 || isOpenRaw === '1';
        setIsDisabled(!isOpen);
      } else {
        setIsDisabled(true);
      }
    } catch (e) {
      console.log('fetchSystemOpen error:', e.message);
    }
  }, [fac_id, years]);

  const fetchDataInfo = useCallback(async () => {
    try {
      const res = await axios.get(config.urlApi + `/activity/showPeriod/${fac_id}/${years - 543}`);
      setInfos(res.data);
      if (res.data.length > 0) {
        const { faculty, employee_amount, building_area, comment } = res.data[0];
        setLatitude(faculty.latitude);
        setLongitude(faculty.longitude);
        setEmployee(employee_amount); // กำหนดค่าเริ่มต้นให้ state employee
        setArea(building_area); // กำหนดค่าเริ่มต้นให้ state area
        setComment(comment);
      }
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: e.message
      });
    }
  }, [fac_id, years]);

  const handlerSubmitUpdate = async (event) => {
    try {
      event.preventDefault();


      const payload = {
        employee_amount: employee,
        building_area: area
      };

      const confirmation = await Swal.fire({
        icon: 'question',
        title: 'Question',
        text: 'ต้องการบันทึกข้อมูลใช่หรือไม่?',
        showCancelButton: true
      });

      if (confirmation.isConfirmed) {
        await Swal.fire({
          icon: 'success',
          title: 'บันทึก',
          text: 'บันทึกข้อมูลสำเร็จ',
          showConfirmButton: false,
          timer: 1500,
          timerProgressBar: true
        });

        await axios.put(config.urlApi + `/activity/modifyDataPeriod/${id}`, payload);
        fetchDataInfo();
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message
      });
    }
  };

  const onLoadDataActivity = useCallback(async () => {
    try {
      const resDataCateScopes = await axios.get(`${config.urlApi}/categoryScopes/central`);
      const existingData = await axios.get(`${config.urlApi}/scope/datasocpe/${id}`, {
        validateStatus: (status) => status === 200 || status === 404,
      });

      const existingRecords = existingData.status === 404 ? [] : (existingData.data || []);
      const existingCategories = [...new Set(existingRecords.map(record => record.name))];

      const categories = resDataCateScopes?.data.filter(cateScope => 
        !existingCategories.includes(cateScope.name)
      );

      if (categories.length <= 0) return;

      Swal.fire({
        title: "Downloading ข้อมูลกิจกรรมการปล่อยก๊าซเรือนกระจก",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const res = await axios.post(`${config.urlApi}/generateActivity`, {
        activityperiod_id: id,
        fac_id: fac_id,
        campus_id: campus_id
      });

      if (res.status === 200) {
        Swal.close();
        fetchDataInfo();
        onDataUpdated();
        Swal.fire({
          title: 'โหลดข้อมูลเสร็จเรียบร้อยแล้ว',
          timer: 1200,
          timerProgressBar: true,
        });
      } else {
        throw new Error('ไม่สามารถสร้างข้อมูลกิจกรรมได้');
      }
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: e.message
      });
    }
  }, [campus_id, fac_id, fetchDataInfo, id, onDataUpdated]);

  useEffect(() => {
    fetchDataInfo();
    onLoadDataActivity();
  }, [fetchDataInfo, onLoadDataActivity]);

  useEffect(() => {
    fetchSystemOpen();
  }, [fetchSystemOpen]);

  const currentInfo = infos[0];

  const overviewItems = useMemo(() => {
    if (!currentInfo) {
      return [];
    }

    return [
      {
        icon: <FaBuilding />,
        label: 'วิทยาเขต',
        value: currentInfo.campus?.campus_name || '-',
      },
      {
        icon: <FaBuilding />,
        label: 'หน่วยงาน',
        value: currentInfo.faculty?.fac_name || '-',
      },
      {
        icon: <FaUsers />,
        label: 'จำนวนพนักงาน',
        value: employee || currentInfo.employee_amount || '-',
      },
      {
        icon: <FaRulerCombined />,
        label: 'พื้นที่ใช้สอย',
        value: area || currentInfo.building_area || '-',
      },
    ];
  }, [area, currentInfo, employee]);

  if (!currentInfo) {
    return (
      <div className="app-info-page">
        <h2 className='text-center'>ข้อมูลทั่วไป</h2>
        <div className="app-empty-state app-empty-state--top app-empty-state--content">
          <div className="app-empty-state__badge">ยังไม่มีข้อมูลทั่วไป</div>
          <h3 className="app-empty-state__title">ยังไม่พบข้อมูลพื้นฐานของหน่วยงานในส่วนนี้</h3>
          <p className="app-empty-state__description">
            ระบบยังไม่มีข้อมูลทั่วไปของหน่วยงานสำหรับรอบปีนี้ เช่น จำนวนพนักงาน พื้นที่ใช้สอย หรือรายละเอียดประกอบการบันทึกข้อมูล
          </p>
          <p className="app-empty-state__hint">
            เมื่อข้อมูลถูกบันทึกหรือซิงก์เข้าระบบแล้ว รายละเอียดของหน่วยงานจะปรากฏในหน้านี้โดยอัตโนมัติ
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="app-info-page">
      <h2 className='text-center'>ข้อมูลทั่วไป</h2>

      <section className="app-info-hero">
        <div className="app-info-hero__copy">
          <span className="app-info-eyebrow">General Overview</span>
          <h3>{currentInfo.faculty?.fac_name || 'หน่วยงาน'}</h3>
          <p>ตรวจสอบข้อมูลพื้นฐานของหน่วยงาน แก้ไขจำนวนพนักงานและพื้นที่ใช้สอย พร้อมดูตำแหน่งบนแผนที่ได้ในหน้าเดียว</p>
        </div>
        <div className="app-info-grid">
          {overviewItems.map((item) => (
            <div key={item.label} className="app-info-stat">
              <span className="app-info-stat__icon">{item.icon}</span>
              <div>
                <small>{item.label}</small>
                <strong>{item.value}</strong>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="row g-4">
        <div className="col-12 col-xl-5">
          <section className="app-info-panel app-info-panel--meta">
            <div className="app-info-panel__head">
              <div>
                <span className="app-info-panel__kicker">Profile</span>
                <h4>รายละเอียดหน่วยงาน</h4>
              </div>
            </div>

            <div className="app-info-meta-list">
              <div className="app-info-meta-item">
                <span>วิทยาเขต</span>
                <strong>{currentInfo.campus?.campus_name || '-'}</strong>
              </div>
              <div className="app-info-meta-item">
                <span>หน่วยงาน</span>
                <strong>{currentInfo.faculty?.fac_name || '-'}</strong>
              </div>
              <div className="app-info-meta-item">
                <span><FaMapMarkerAlt /> Latitude</span>
                <strong>{latitude || '-'}</strong>
              </div>
              <div className="app-info-meta-item">
                <span><FaMapMarkerAlt /> Longitude</span>
                <strong>{longitude || '-'}</strong>
              </div>
              <div className="app-info-meta-item">
                <span>รูปแบบการคำนวณ</span>
                <strong>องค์การบริหารจัดการก๊าซเรือนกระจก (องค์การมหาชน)</strong>
              </div>
              <div className="app-info-meta-item">
                <span>ปีฐาน</span>
                <strong>2565</strong>
              </div>
              <div className="app-info-meta-item app-info-meta-item--comment">
                <span><FaCommentDots /> ข้อความเห็น</span>
                <strong>{comment || '-'}</strong>
              </div>
            </div>
          </section>
        </div>

        <div className="col-12 col-xl-7">
          <section className="app-info-panel app-info-panel--form">
            <div className="app-info-panel__head">
              <div>
                <span className="app-info-panel__kicker">Update</span>
                <h4>แก้ไขข้อมูลหลักของหน่วยงาน</h4>
              </div>
            </div>

            <form onSubmit={handlerSubmitUpdate} className="app-info-form">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label" htmlFor="employee_amount">จำนวนพนักงาน</label>
                  <input
                    id="employee_amount"
                    type="number"
                    className="form-control"
                    value={employee}
                    onChange={(e) => setEmployee(e.target.value)}
                    required
                    disabled={isDisabled}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label" htmlFor="building_area">พื้นที่ใช้สอย</label>
                  <input
                    id="building_area"
                    type="number"
                    className="form-control"
                    value={area}
                    step="any"
                    onChange={(e) => setArea(e.target.value)}
                    required
                    disabled={isDisabled}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label" htmlFor="latitude_view">Latitude</label>
                  <input id="latitude_view" type="text" className="form-control" disabled value={latitude} />
                </div>

                <div className="col-md-6">
                  <label className="form-label" htmlFor="longitude_view">Longitude</label>
                  <input id="longitude_view" type="text" className="form-control" disabled value={longitude} />
                </div>
              </div>

              <div className="app-info-form__hint">
                <span className={`app-info-status ${isDisabled ? 'is-locked' : 'is-ready'}`}>
                  {isDisabled ? 'ช่วงเวลานี้ยังไม่เปิดให้แก้ไขข้อมูล' : 'พร้อมสำหรับการบันทึกข้อมูล'}
                </span>
              </div>

              <div className="app-info-form__actions">
                <button className="btn btn-primary" disabled={isDisabled}>บันทึกข้อมูล</button>
              </div>
            </form>
          </section>
        </div>
      </div>

      <section className="app-info-panel app-info-panel--map">
        <div className="app-info-panel__head">
          <div>
            <span className="app-info-panel__kicker">Map</span>
            <h4>ตำแหน่งและที่อยู่ของหน่วยงาน</h4>
          </div>
        </div>

        <Map
          detail={`วิทยาเขต ${currentInfo.campus?.campus_name} หน่วยงาน ${currentInfo.faculty?.fac_name}`}
          address={`ที่อยู่ ${currentInfo.faculty?.address || '-'}`}
          latitude={parseFloat(currentInfo.faculty?.latitude)}
          longitude={parseFloat(currentInfo.faculty?.longitude)}
          setLatitude={setLatitude}
          setLongitude={setLongitude}
        />
      </section>
    </div>
  );
}

export default TabActivityInfo;
